<script setup lang="ts">
/**
 * Full-card cell for the public clients statement table's mobile view: one
 * client's name, latest balance, and business-services breakdown — the
 * whole "card" is a single wide column, so the outer DynamicTable renders
 * one row per client while still reading as a stacked card list on a narrow
 * screen (each `<tr>` is one card). The breakdown itself is a nested
 * DynamicTable (TextCell/MoneyCell columns), matching this app's
 * "DynamicTable everywhere" convention — same structure as
 * `StuffMobileCardCell`, minus the lessons/income/unique-clients summary and
 * gender balance, which aren't part of this report.
 */
import { computed, markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import { majorToMinor } from '@/utils/majorToMinor'

const { t } = useI18n()

interface ServiceRow {
  dateFormatted: string
  serviceName: string
  price: number
}

defineProps<{
  /** Client display name. */
  name: string
  /** Pre-formatted latest balance, e.g. "$1,907.00" or "–" when unknown. */
  balanceFormatted: string
  /** Sign of the raw balance amount, for coloring; `undefined` when unknown. */
  balanceSign?: 'positive' | 'negative' | 'zero'
  /** One entry per business service, sorted by date, with pre-formatted date. */
  businessServices: ServiceRow[]
}>()

const serviceColumns = computed<Column<ServiceRow>[]>(() => [
  { key: 'dateFormatted', label: t('common.date'), component: markRaw(TextCell) },
  { key: 'serviceName', label: t('common.service'), component: markRaw(TextCell) },
  {
    key: 'price',
    label: t('common.price'),
    component: markRaw(MoneyCell),
    props: (bs) => ({ amount: majorToMinor(bs.price) }),
    align: 'right',
  },
])
</script>

<template>
  <td class="client-statement-mobile-card-cell font-tufte">
    <div class="client-statement-mobile-card-cell__name">{{ name }}</div>
    <div class="client-statement-mobile-card-cell__summary">
      <span class="client-statement-mobile-card-cell__lbl">{{ t('common.balance') }}</span>
      <span
        class="client-statement-mobile-card-cell__val"
        :class="{
          'client-statement-mobile-card-cell__val--positive': balanceSign === 'positive',
          'client-statement-mobile-card-cell__val--negative': balanceSign === 'negative',
        }"
        >{{ balanceFormatted }}</span
      >
    </div>
    <DynamicTable
      class="client-statement-mobile-card-cell__lessons"
      :columns="serviceColumns as Column<unknown>[]"
      :data="businessServices"
      :pinnable-header="false"
    />
  </td>
</template>

<style scoped>
/*
 * `!important`: DynamicTable's own internal `.dynamic-table :deep(td)` rule
 * (one class + one type selector) outranks a plain
 * `.client-statement-mobile-card-cell` class selector on specificity, so it
 * would otherwise win over this card-sized padding regardless of source
 * order — same reasoning as `StuffMobileCardCell`.
 */
.client-statement-mobile-card-cell {
  vertical-align: top;
  padding: 0.6rem 1rem !important;
}

.client-statement-mobile-card-cell__name {
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-text);
}

.client-statement-mobile-card-cell__summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-family: var(--font-tufte-sans);
}

.client-statement-mobile-card-cell__lbl {
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.client-statement-mobile-card-cell__val {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-tufte-text);
}

.client-statement-mobile-card-cell__val--positive {
  color: var(--color-money-positive);
}

.client-statement-mobile-card-cell__val--negative {
  color: var(--color-money-negative);
}

.client-statement-mobile-card-cell__lessons {
  margin-top: 0.6rem;
  border-top: 1px solid color-mix(in srgb, var(--color-tufte-border) 50%, transparent);
}

.client-statement-mobile-card-cell__lessons :deep(.dynamic-table) {
  width: 100%;
  font-size: var(--text-tufte-xs);
}

.client-statement-mobile-card-cell__lessons :deep(.dynamic-table th) {
  padding: 0.25rem 0.5rem 0.25rem 0;
  font-size: 0.62rem;
}

.client-statement-mobile-card-cell__lessons :deep(.dynamic-table td) {
  padding: 0.3rem 0.5rem 0.3rem 0;
}
</style>
